import { toggleWorkoutState } from "@services/metadataService";
import { Step, Workout } from "./planTypes";
import { SportType } from '@utils/planTypes';

export interface RestDay {
  id: string;
  name: string;
  type: string;
  week_number: number;
  day_number: number;
  date: Date;
  isRestDay: boolean;
}

export interface WorkoutWithMetadata {
  uuid: string;
  id: string;
  name: string;
  totalDuration: number;
  totalDistance: number;
  type: SportType;
  isKeySession: boolean;
  isSequence: boolean;
  week_number: number;
  day_number: number;
  original_day: number;
  completed: boolean;
  hidden: boolean;
  date: Date;
  isRestDay?: boolean;
  steps: unknown[];
  coachComment: string;
  nutritionComment: string;
  description: string;
  isManuallyAdded?: boolean;
}

export interface MetadataProps {
  completed?: boolean;
  hidden?: boolean;
  completed_at?: Date | null;
  comment?: string;
}

interface HandleWorkoutStateProps {
  user_id: string;
  plan_id: string;
  uuid: string;
  metadata: MetadataProps;
  setMetadata: (metadata: MetadataProps) => void,
}

export const unitToWording = (unit: string, value: string | number) => {
  const numericValue = Number(value);

  if (unit === 'time') {
    let hours = Math.floor(numericValue / 3600);
    let minutes = Math.floor((numericValue % 3600) / 60);
    let seconds = Math.round(numericValue % 60);
    if (seconds === 60) {
      seconds = 0;
      minutes += 1;
    }
    if (minutes === 60) {
      minutes = 0;
      hours += 1;
    }

    const parts: string[] = [];

    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}'`);
    if (seconds > 0) parts.push(`${seconds}`);

    if (hours === 0 && minutes === 0) {
      return `${seconds}"`;
    }

    return parts.join('');
  }

  if (unit === 'distance') {
    return numericValue % 1 === 0
      ? `${numericValue.toFixed(0)}m`
      : `${numericValue}m`;
  }

  return `${value} ${unit}`;
};

export const sumWorkoutTimeDurations = (workout: Workout): number => {
  const sumTimeDurations = (step: Step): number => {
    const currentDuration = step.durationType === "time" ? Number(step.durationValue) : 0;
    const nestedDuration = step.steps?.reduce((sum, subStep) => sum + sumTimeDurations(subStep), 0) || 0;
    return currentDuration + nestedDuration;
  };

  return workout.steps.reduce((total, step) => total + sumTimeDurations(step), 0);
};

export const sumWorkoutDistanceDurations = (workout: Workout): number => {
  const sumDistanceDurations = (step: Step): number => {
    if (step.isRepeat && step.steps) {
      const nestedDistance = step.steps.reduce(
        (sum, subStep) => sum + sumDistanceDurations(subStep),
        0
      );
      return (step.repeatCount || 1) * nestedDistance;
    }
    return step.durationType === 'distance' ? Number(step.durationValue) : 0;
  };
  return workout.steps.reduce((total, step) => total + sumDistanceDurations(step), 0);
};

export const averageIntensityForMainSteps = (workout: Workout): string => {
  const zoneToNumber = (zone: string): number => parseInt(zone.split(" ")[1]);
  const numberToZone = (zoneValue: number): string => `Zone ${Math.round(zoneValue)}`;

  const collectIntensities = (step: Step, intensities: number[] = []): number[] => {
    if (step.type !== "Échauffement" && step.type !== "Récupération") {
      if (step.type !== "Répétition") intensities.push(zoneToNumber(step.intensity));
      if (step.steps) step.steps.forEach((subStep) => collectIntensities(subStep, intensities));
    }
    return intensities;
  };

  const allIntensities = workout.steps.reduce(
    (intensities, step) => collectIntensities(step, intensities),
    [] as number[]
  );

  const averageZoneValue = allIntensities.reduce((sum, intensity) => sum + intensity, 0) / allIntensities.length;

  return numberToZone(averageZoneValue);
};

export const getIntensityColor = (intensity: string): string => {
    switch (intensity) {
      case "Repos":
        return "white";
      case "Zone 1":
      case "Z1":
        return "gray";
      case "Zone 2":
      case "Z2":
        return "blue";
      case "Zone 3":
      case "Z3":
        return "green";
      case "Zone 4":
      case "Z4":
        return "yellow";
      case "Zone 5":
      case "Z5":
        return "orange";
      case "Zone 6":
      case "Z6":
        return "red";
      case "Zone 7":
      case "Z7":
        return "purple";
      default:
        return "808080";
    }
};

export const updateWorkoutState = ({ user_id, plan_id, uuid, metadata, setMetadata }: HandleWorkoutStateProps) => {
  if (!user_id || !plan_id || !uuid || !metadata || !setMetadata) return;

  const isCompleted = metadata.completed_at !== null;

  toggleWorkoutState(user_id, plan_id, uuid, !isCompleted);
  setMetadata({
      ...metadata,
      completed_at: isCompleted ? null : new Date(),
  });
};

export const groupConsecutiveStepsByType = (steps: Step[]): (Step | Step[])[] => {
    const grouped: (Step | Step[])[] = [];
    let buffer: Step[] = [];

    for (let i = 0; i < steps.length; i++) {
        const curr = steps[i];

        if (
            buffer.length === 0 ||
            (curr.type === buffer[0].type && !curr.isRepeat && !buffer[0].isRepeat)
        ) {
            buffer.push(curr);
        } else {
            grouped.push(buffer.length > 1 ? [...buffer] : buffer[0]);
            buffer = [curr];
        }
    }

    if (buffer.length) {
        grouped.push(buffer.length > 1 ? [...buffer] : buffer[0]);
    }

    return grouped;
}

export const shortPlanName = (title: string): string => {
  if (title.length === 0) { return title; }
  const parts = title.split(" - ");
  parts.pop();
  return parts.join(" - ");
};

export function isWorkoutWithMetadata(workout: WorkoutWithMetadata | RestDay): workout is WorkoutWithMetadata {
  return !workout.isRestDay;
}
